import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import CountDonut from "@/components/CountDonut";
import {
  Package,
  Plus,
  ArrowDownToLine,
  ArrowUpFromLine,
  AlertTriangle,
  Trash2,
  Pencil,
  Download,
  Utensils,
  UserRound,
  RotateCcw,
  CheckCircle2,
} from "lucide-react";

function isoDay(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}

const emptyItem = { name: "", category: "Store", unit: "kg", min_stock: 0, initial_stock: 0 };
const emptyTxn = { item_id: "", txn_type: "in", quantity: "", manager: "", note: "", department: "", handed_to: "" };
const KITCHEN_DEPTS = ["Tandoor", "Indian", "Continental", "Chinese", "Bakery", "Bar", "Store", "Other"];
const TXN_LABELS = { in: "Stock in", out: "Stock out", close: "Closing return", used: "Utilised" };

export default function InventoryPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const isManager = Boolean(user?.manager_name);

  const [tab, setTab] = useState("all"); // 'all' | one of categories
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Transactions
  const [txns, setTxns] = useState([]);
  const [txnFilterType, setTxnFilterType] = useState("all");
  const [txnStart, setTxnStart] = useState(isoDay(-29));
  const [txnEnd, setTxnEnd] = useState(isoDay(0));

  const [itemDialog, setItemDialog] = useState(false);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingItem, setEditingItem] = useState(null);
  const [itemSaving, setItemSaving] = useState(false);

  const [txnDialog, setTxnDialog] = useState(false);
  const [txnForm, setTxnForm] = useState(emptyTxn);
  const [txnSaving, setTxnSaving] = useState(false);
  const [downloadingIn, setDownloadingIn] = useState(false);
  const [downloadingOut, setDownloadingOut] = useState(false);
  const [utilisedQty, setUtilisedQty] = useState("");
  const [outToday, setOutToday] = useState(null);

  // Department consumption (owner-only donut) + Handover log
  const [deptData, setDeptData] = useState({ by_department: [], total_entries: 0 });
  const [handover, setHandover] = useState({ entries: [], names: [], count: 0 });
  const [handoverName, setHandoverName] = useState("all");
  const [handoverDays, setHandoverDays] = useState(7);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab !== "all" ? { category: tab } : {};
      const [{ data: cats }, { data: its }, { data: sum }, { data: mgrs }] = await Promise.all([
        api.get("/inventory/categories"),
        api.get("/inventory/items", { params }),
        api.get("/inventory/summary"),
        api.get("/expenses/managers").catch(() => ({ data: { managers: [] } })),
      ]);
      setCategories(cats.categories);
      setItems(its);
      setSummary(sum);
      setManagers(mgrs.managers || []);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const loadTxns = useCallback(async () => {
    const params = { start_date: txnStart, end_date: txnEnd };
    if (tab !== "all") params.category = tab;
    if (txnFilterType !== "all") params.txn_type = txnFilterType;
    const { data } = await api.get("/inventory/transactions", { params });
    setTxns(data);
  }, [tab, txnFilterType, txnStart, txnEnd]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => { loadTxns(); }, [loadTxns]);

  // Load department consumption for the current month
  useEffect(() => {
    if (!isOwner) return;
    const today = new Date();
    api.get("/inventory/dept-consumption", { params: { year: today.getFullYear(), month: today.getMonth() + 1 } })
      .then(({ data }) => setDeptData(data))
      .catch(() => {});
  }, [isOwner, txns.length]);

  // Load handover log
  useEffect(() => {
    const params = { days: handoverDays };
    if (handoverName !== "all") params.handed_to = handoverName;
    api.get("/inventory/handover-log", { params })
      .then(({ data }) => setHandover(data))
      .catch(() => {});
  }, [handoverName, handoverDays, txns.length]);

  const openNewItem = () => {
    setEditingItem(null);
    setItemForm({ ...emptyItem, category: tab !== "all" ? tab : "Store" });
    setItemDialog(true);
  };

  const openEditItem = (item) => {
    setEditingItem(item);
    setItemForm({
      name: item.name,
      category: item.category,
      unit: item.unit || "pcs",
      min_stock: item.min_stock || 0,
      initial_stock: 0, // read-only after create
    });
    setItemDialog(true);
  };

  const saveItem = async (e) => {
    e.preventDefault();
    setItemSaving(true);
    try {
      if (editingItem) {
        await api.put(`/inventory/items/${editingItem.id}`, {
          name: itemForm.name,
          category: itemForm.category,
          unit: itemForm.unit,
          min_stock: parseFloat(itemForm.min_stock) || 0,
        });
        toast.success("Item updated");
      } else {
        await api.post("/inventory/items", {
          name: itemForm.name,
          category: itemForm.category,
          unit: itemForm.unit,
          min_stock: parseFloat(itemForm.min_stock) || 0,
          initial_stock: parseFloat(itemForm.initial_stock) || 0,
        });
        toast.success("Item added");
      }
      setItemDialog(false);
      await loadAll();
      await loadTxns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setItemSaving(false);
    }
  };

  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This also removes its transaction history.`)) return;
    try {
      await api.delete(`/inventory/items/${item.id}`);
      toast.success("Item removed");
      await loadAll();
      await loadTxns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const openNewTxn = (type, item = null) => {
    setTxnForm({
      item_id: item?.id || (items[0]?.id || ""),
      txn_type: type,
      quantity: "",
      manager: isManager ? user.manager_name : (managers[0] || ""),
      note: "",
      department: type === "out" || type === "close" ? "Indian" : "",
      handed_to: "",
    });
    setUtilisedQty("");
    setOutToday(null);
    setTxnDialog(true);
  };

  // Fetch today's stock-out qty for the selected item when txn_type is close.
  useEffect(() => {
    if (!txnDialog || txnForm.txn_type !== "close" || !txnForm.item_id) {
      setOutToday(null);
      return;
    }
    api.get("/inventory/daily-wastage")
      .then(({ data }) => {
        const row = (data.rows || []).find((r) => r.item_id === txnForm.item_id);
        setOutToday(row ? row.out_qty : 0);
      })
      .catch(() => setOutToday(null));
  }, [txnDialog, txnForm.txn_type, txnForm.item_id]);

  const suggestedWastage = React.useMemo(() => {
    if (txnForm.txn_type !== "close" || outToday == null) return null;
    const used = parseFloat(utilisedQty) || 0;
    const closed = parseFloat(txnForm.quantity) || 0;
    return Math.max(0, Math.round((outToday - used - closed) * 1000) / 1000);
  }, [outToday, utilisedQty, txnForm.quantity, txnForm.txn_type]);

  const saveTxn = async (e) => {
    e.preventDefault();
    if ((txnForm.txn_type === "out" || txnForm.txn_type === "close") && !txnForm.department) {
      toast.error("Pick which department");
      return;
    }
    setTxnSaving(true);
    try {
      // For closing return: log a "used" txn first (if utilised > 0), then the "close" txn.
      if (txnForm.txn_type === "close" && parseFloat(utilisedQty) > 0) {
        const usedPayload = {
          item_id: txnForm.item_id,
          txn_type: "used",
          quantity: parseFloat(utilisedQty),
          note: txnForm.note ? `${txnForm.note} · utilised` : "utilised",
          department: txnForm.department,
          handed_to: txnForm.handed_to,
        };
        if (!isManager) usedPayload.manager = txnForm.manager;
        await api.post("/inventory/transactions", usedPayload);
      }

      const payload = {
        item_id: txnForm.item_id,
        txn_type: txnForm.txn_type,
        quantity: parseFloat(txnForm.quantity),
        note: txnForm.note,
      };
      if (!isManager) payload.manager = txnForm.manager;
      if (txnForm.txn_type === "out" || txnForm.txn_type === "close") {
        payload.department = txnForm.department;
        payload.handed_to = txnForm.handed_to;
      }
      await api.post("/inventory/transactions", payload);
      const msg = txnForm.txn_type === "in" ? "Stock in recorded"
                : txnForm.txn_type === "close" ? "Closing recorded (utilised + return + wastage tracked)"
                : "Stock out recorded";
      toast.success(msg);
      setTxnDialog(false);
      await loadAll();
      await loadTxns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setTxnSaving(false);
    }
  };

  const downloadMonthly = async (direction) => {
    const setter = direction === "in" ? setDownloadingIn : setDownloadingOut;
    setter(true);
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const params = { year, month, direction };
      if (tab !== "all") params.category = tab;
      const res = await api.get("/inventory/transactions/monthly-pdf", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-stock-${direction}-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded Stock ${direction.toUpperCase()} PDF`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally { setter(false); }
  };

  const [downloadingWastage, setDownloadingWastage] = useState(false);
  const downloadWastage = async () => {
    setDownloadingWastage(true);
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const res = await api.get("/inventory/wastage-pdf", { params: { year, month }, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-wastage-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Downloaded Wastage PDF");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally { setDownloadingWastage(false); }
  };

  const lowStockCount = summary?.low_stock_count || 0;

  return (
    <div data-testid="inventory-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Stock</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Inventory</h1>
          <p className="mt-3 text-muted-foreground">
            Every stock in and stock out is tagged with the manager who did it. Owner sees the full log.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            onClick={() => downloadMonthly("in")}
            disabled={downloadingIn}
            data-testid="download-stock-in-btn"
            variant="outline"
            className="rounded-full"
          >
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            {downloadingIn ? "…" : "Stock IN PDF"}
          </Button>
          <Button
            onClick={() => downloadMonthly("out")}
            disabled={downloadingOut}
            data-testid="download-stock-out-btn"
            variant="outline"
            className="rounded-full"
          >
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            {downloadingOut ? "…" : "Stock OUT PDF"}
          </Button>
          <Button
            onClick={downloadWastage}
            disabled={downloadingWastage}
            data-testid="download-wastage-btn"
            variant="outline"
            className="rounded-full"
          >
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            {downloadingWastage ? "…" : "Wastage PDF"}
          </Button>
          <Button
            onClick={() => openNewTxn("in")}
            disabled={items.length === 0}
            data-testid="stock-in-btn"
            variant="outline"
            className="rounded-full"
          >
            <ArrowDownToLine className="w-4 h-4 mr-2" aria-hidden="true" /> Stock in
          </Button>
          <Button
            onClick={() => openNewTxn("out")}
            disabled={items.length === 0}
            data-testid="stock-out-btn"
            variant="outline"
            className="rounded-full"
          >
            <ArrowUpFromLine className="w-4 h-4 mr-2" aria-hidden="true" /> Stock out
          </Button>
          <Button
            onClick={() => openNewTxn("close")}
            disabled={items.length === 0}
            data-testid="closing-stock-btn"
            variant="outline"
            className="rounded-full"
          >
            <RotateCcw className="w-4 h-4 mr-2" aria-hidden="true" /> Closing return
          </Button>
          <Button
            onClick={openNewItem}
            data-testid="add-item-btn"
            className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> New item
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
        {categories.map((c) => {
          const stat = summary?.categories?.find((x) => x.category === c) || { items: 0, low: 0 };
          const active = tab === c;
          return (
            <button
              type="button"
              key={c}
              onClick={() => setTab(active ? "all" : c)}
              data-testid={`cat-tile-${c}`}
              className={`text-left p-4 border transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-white border-border hover:border-primary"
              }`}
            >
              <div className={`text-[10px] uppercase tracking-widest ${active ? "opacity-70" : "text-muted-foreground"}`}>
                {c}
              </div>
              <div className="font-serif text-2xl mt-1">{stat.items}</div>
              <div className={`text-xs mt-1 ${active ? "opacity-80" : "text-muted-foreground"}`}>
                {stat.low > 0 ? `${stat.low} low` : "healthy"}
              </div>
            </button>
          );
        })}
      </div>

      {lowStockCount > 0 && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-800 p-4 flex items-start gap-3" data-testid="low-stock-banner">
          <AlertTriangle className="w-5 h-5 mt-0.5" aria-hidden="true" />
          <div className="flex-1">
            <div className="font-medium">{lowStockCount} item{lowStockCount > 1 ? "s" : ""} at or below minimum stock</div>
            <div className="text-sm opacity-80">
              {summary.low_stock.slice(0, 4).map((i) => `${i.name} (${i.current_stock}${i.unit})`).join(" · ")}
            </div>
          </div>
        </div>
      )}

      {/* Items table */}
      <div className="mt-6 bg-white border border-border fade-up overflow-x-auto">
        <div className="px-6 py-4 border-b border-border flex items-baseline justify-between">
          <h2 className="font-serif text-2xl tracking-tight">
            Items {tab !== "all" && <span className="text-muted-foreground text-lg">· {tab}</span>}
          </h2>
          <span className="text-xs text-muted-foreground">{items.length} shown</span>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-14 text-center">
            <p className="font-serif text-2xl">No items yet.</p>
            <p className="text-muted-foreground mt-2">Tap "New item" to add your first entry.</p>
          </div>
        ) : (
          <table className="w-full min-w-[720px]" data-testid="items-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <th className="px-6 py-3 font-normal">Item</th>
                <th className="px-6 py-3 font-normal">Category</th>
                <th className="px-6 py-3 font-normal text-right">In stock</th>
                <th className="px-6 py-3 font-normal text-right">Min</th>
                <th className="px-6 py-3 font-normal text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i) => {
                const low = Number(i.current_stock || 0) <= Number(i.min_stock || 0);
                return (
                  <tr key={i.id} className="border-t border-border" data-testid={`item-row-${i.id}`}>
                    <td className="px-6 py-3 font-medium">{i.name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{i.category}</td>
                    <td className={`px-6 py-3 text-right font-serif text-lg ${low ? "text-destructive" : ""}`}>
                      {i.current_stock} <span className="text-xs text-muted-foreground">{i.unit}</span>
                    </td>
                    <td className="px-6 py-3 text-right text-muted-foreground">{i.min_stock}</td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => openNewTxn("in", i)} data-testid={`txn-in-${i.id}`} title="Stock in">
                        <ArrowDownToLine className="w-4 h-4" aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openNewTxn("out", i)} data-testid={`txn-out-${i.id}`} title="Stock out">
                        <ArrowUpFromLine className="w-4 h-4" aria-hidden="true" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEditItem(i)} data-testid={`edit-item-${i.id}`}>
                        <Pencil className="w-4 h-4" aria-hidden="true" />
                      </Button>
                      {isOwner && (
                        <Button size="sm" variant="ghost" onClick={() => deleteItem(i)} data-testid={`delete-item-${i.id}`}>
                          <Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Transaction history */}
      <div className="mt-10 fade-up">

      {/* Department consumption donut (owner only) */}
      {isOwner && deptData.total_entries > 0 && (
        <div className="mb-10 bg-white border border-border p-6 fade-up" data-testid="dept-consumption-panel">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Kitchen use</p>
              <h2 className="mt-1 font-serif text-2xl tracking-tight flex items-center gap-2">
                <Utensils className="w-5 h-5 text-primary" /> Department consumption
              </h2>
            </div>
            <span className="text-xs text-muted-foreground">
              {deptData.total_entries} stock-outs this month
            </span>
          </div>
          <CountDonut
            testId="dept-donut"
            unit="stock-outs"
            data={deptData.by_department.map((d) => ({
              label: d.department,
              count: d.count,
              subtitle: d.top_items?.slice(0, 2).map((t) => t.name).join(" · "),
            }))}
          />
        </div>
      )}

      {/* Handover log */}
      <div className="mb-10 bg-white border border-border p-6 fade-up" data-testid="handover-log-panel">
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Accountability</p>
            <h2 className="mt-1 font-serif text-2xl tracking-tight flex items-center gap-2">
              <UserRound className="w-5 h-5 text-primary" /> Kitchen handover log
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">Every item handed to a cook — sorted newest first.</p>
          </div>
          <div className="flex gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Person</Label>
              <Select value={handoverName} onValueChange={setHandoverName}>
                <SelectTrigger data-testid="handover-name" className="mt-1 h-9 w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All cooks</SelectItem>
                  {handover.names.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Days</Label>
              <Select value={String(handoverDays)} onValueChange={(v) => setHandoverDays(Number(v))}>
                <SelectTrigger data-testid="handover-days" className="mt-1 h-9 w-24"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Today</SelectItem>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {handover.entries.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground text-sm">
            No handovers in this range{handoverName !== "all" ? ` for ${handoverName}` : ""}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm" data-testid="handover-log-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="py-2 pr-4 font-normal">When</th>
                  <th className="py-2 pr-4 font-normal">Cook</th>
                  <th className="py-2 pr-4 font-normal">Item</th>
                  <th className="py-2 pr-4 font-normal text-right">Qty</th>
                  <th className="py-2 pr-4 font-normal">Dept</th>
                  <th className="py-2 pr-4 font-normal">Given by</th>
                </tr>
              </thead>
              <tbody>
                {handover.entries.slice(0, 100).map((h) => (
                  <tr key={h.created_at + h.item_name} className="border-t border-border">
                    <td className="py-2 pr-4 text-muted-foreground whitespace-nowrap">
                      {new Date(h.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="py-2 pr-4 font-medium">{h.handed_to || "—"}</td>
                    <td className="py-2 pr-4">{h.item_name}</td>
                    <td className="py-2 pr-4 text-right font-serif">
                      {h.quantity} <span className="text-xs text-muted-foreground">{h.unit}</span>
                    </td>
                    <td className="py-2 pr-4">
                      <span className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800">
                        {h.department || "—"}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">{h.manager || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {handover.entries.length > 100 && (
              <p className="mt-2 text-xs text-muted-foreground text-right">Showing first 100 of {handover.entries.length}</p>
            )}
          </div>
        )}
      </div>
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-serif text-2xl tracking-tight">Transaction history</h2>
          <div className="flex gap-3 items-end">
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">From</Label>
              <Input type="date" data-testid="txn-start" value={txnStart} onChange={(e) => setTxnStart(e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">To</Label>
              <Input type="date" data-testid="txn-end" value={txnEnd} onChange={(e) => setTxnEnd(e.target.value)} className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Type</Label>
              <Select value={txnFilterType} onValueChange={setTxnFilterType}>
                <SelectTrigger className="mt-1 h-9 w-28" data-testid="txn-filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="in">Stock in</SelectItem>
                  <SelectItem value="out">Stock out</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="bg-white border border-border overflow-x-auto">
          {txns.length === 0 ? (
            <div className="p-14 text-center text-muted-foreground">No transactions in this range.</div>
          ) : (
            <table className="w-full min-w-[720px]" data-testid="txns-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <th className="px-6 py-3 font-normal">Date</th>
                  <th className="px-6 py-3 font-normal">Item</th>
                  <th className="px-6 py-3 font-normal">Category</th>
                  <th className="px-6 py-3 font-normal">Type</th>
                  <th className="px-6 py-3 font-normal text-right">Qty</th>
                  <th className="px-6 py-3 font-normal">Manager</th>
                  <th className="px-6 py-3 font-normal">Note</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-t border-border" data-testid={`txn-row-${t.id}`}>
                    <td className="px-6 py-3 text-muted-foreground whitespace-nowrap">
                      {new Date(t.created_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-6 py-3 font-medium">{t.item_name}</td>
                    <td className="px-6 py-3 text-muted-foreground">{t.category}</td>
                    <td className="px-6 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full ${
                        t.txn_type === "in" ? "bg-emerald-100 text-emerald-800" :
                        t.txn_type === "close" ? "bg-blue-100 text-blue-800" :
                        t.txn_type === "used" ? "bg-purple-100 text-purple-800" :
                        "bg-amber-100 text-amber-800"
                      }`}>
                        {TXN_LABELS[t.txn_type] || t.txn_type}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-right font-serif text-lg">
                      {t.quantity} <span className="text-xs text-muted-foreground">{t.unit}</span>
                    </td>
                    <td className="px-6 py-3 font-medium">{t.manager}</td>
                    <td className="px-6 py-3 text-muted-foreground max-w-xs truncate">{t.note || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Item dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent data-testid="item-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {editingItem ? "Edit item" : "New item"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveItem} className="space-y-4">
            <div>
              <Label htmlFor="iname">Name</Label>
              <Input id="iname" data-testid="item-name" required value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select value={itemForm.category} onValueChange={(v) => setItemForm({ ...itemForm, category: v })}>
                  <SelectTrigger data-testid="item-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="unit">Unit</Label>
                <Input id="unit" data-testid="item-unit" value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} placeholder="kg, ltr, pcs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="min">Min stock (alert)</Label>
                <Input id="min" type="number" min="0" step="0.01" data-testid="item-min" value={itemForm.min_stock} onChange={(e) => setItemForm({ ...itemForm, min_stock: e.target.value })} />
              </div>
              {!editingItem && (
                <div>
                  <Label htmlFor="init">Opening stock</Label>
                  <Input id="init" type="number" min="0" step="0.01" data-testid="item-initial" value={itemForm.initial_stock} onChange={(e) => setItemForm({ ...itemForm, initial_stock: e.target.value })} />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setItemDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={itemSaving} data-testid="save-item-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {itemSaving ? "Saving…" : editingItem ? "Save" : "Add item"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Transaction dialog */}
      <Dialog open={txnDialog} onOpenChange={setTxnDialog}>
        <DialogContent data-testid="txn-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {txnForm.txn_type === "in" ? "Stock in" : "Stock out"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={saveTxn} className="space-y-4">
            <div>
              <Label>Item</Label>
              <Select value={txnForm.item_id} onValueChange={(v) => setTxnForm({ ...txnForm, item_id: v })}>
                <SelectTrigger data-testid="txn-item">
                  <SelectValue placeholder="Pick an item" />
                </SelectTrigger>
                <SelectContent>
                  {items.map((i) => (
                    <SelectItem key={i.id} value={i.id}>
                      {i.name} ({i.category}) · {i.current_stock}{i.unit}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="qty">
                  {txnForm.txn_type === "close" ? "Closing return qty" : "Quantity"}
                </Label>
                <Input id="qty" data-testid="txn-qty" type="number" step="0.01" min="0.01" required value={txnForm.quantity} onChange={(e) => setTxnForm({ ...txnForm, quantity: e.target.value })} />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={txnForm.txn_type} onValueChange={(v) => setTxnForm({ ...txnForm, txn_type: v })}>
                  <SelectTrigger data-testid="txn-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">Stock in</SelectItem>
                    <SelectItem value="out">Stock out</SelectItem>
                    <SelectItem value="close">Closing return (end of day)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* End-of-day comparison block — only for closing return */}
            {txnForm.txn_type === "close" && (
              <div className="p-4 bg-blue-50 border border-blue-200 space-y-3" data-testid="closing-eod-block">
                <div className="text-xs uppercase tracking-widest text-blue-900 font-medium">
                  End of day comparison
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Sent today</div>
                    <div className="font-serif text-xl mt-1" data-testid="eod-out-today">
                      {outToday == null ? "…" : outToday}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Utilised</div>
                    <Input
                      type="number" step="0.01" min="0"
                      value={utilisedQty}
                      onChange={(e) => setUtilisedQty(e.target.value)}
                      data-testid="txn-utilised"
                      className="h-9 text-center mt-1"
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Wastage</div>
                    <div className={`font-serif text-xl mt-1 ${suggestedWastage > 0 ? "text-primary" : "text-emerald-700"}`} data-testid="eod-wastage">
                      {suggestedWastage == null ? "…" : suggestedWastage}
                    </div>
                  </div>
                </div>
                <p className="text-[11px] text-muted-foreground text-center">
                  Wastage = Sent today − Utilised − Closing return. Saved automatically as separate entries.
                </p>
              </div>
            )}
            <div>
              <Label>Manager (who did this)</Label>
              {isManager ? (
                <Input value={user.manager_name} disabled data-testid="txn-manager-readonly" className="bg-secondary" />
              ) : (
                <Select value={txnForm.manager} onValueChange={(v) => setTxnForm({ ...txnForm, manager: v })}>
                  <SelectTrigger data-testid="txn-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                  <SelectContent>
                    {managers.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div>
              <Label htmlFor="note">Note</Label>
              <Textarea id="note" data-testid="txn-note" rows={2} value={txnForm.note} onChange={(e) => setTxnForm({ ...txnForm, note: e.target.value })} placeholder="e.g. from vendor Ram Prasad" />
            </div>

            {/* Stock-OUT / CLOSE specific fields */}
            {(txnForm.txn_type === "out" || txnForm.txn_type === "close") && (
              <div className="grid grid-cols-2 gap-4 p-4 bg-amber-50 border border-amber-200">
                <div className="col-span-2 text-xs uppercase tracking-widest text-amber-800 font-medium">
                  {txnForm.txn_type === "out" ? "Kitchen assignment" : "Returning from"}
                </div>
                <div>
                  <Label htmlFor="dept">Department</Label>
                  <Select value={txnForm.department} onValueChange={(v) => setTxnForm({ ...txnForm, department: v })}>
                    <SelectTrigger id="dept" data-testid="txn-department"><SelectValue placeholder="Which section?" /></SelectTrigger>
                    <SelectContent>
                      {KITCHEN_DEPTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="handed">{txnForm.txn_type === "out" ? "Handed over to" : "Returned by"}</Label>
                  <Input
                    id="handed"
                    data-testid="txn-handed-to"
                    value={txnForm.handed_to}
                    onChange={(e) => setTxnForm({ ...txnForm, handed_to: e.target.value })}
                    placeholder={txnForm.txn_type === "out" ? "e.g. Suneel (Head Chef)" : "e.g. Suneel (Head Chef)"}
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setTxnDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={txnSaving} data-testid="save-txn-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {txnSaving ? "Saving…" : "Record"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
